import type { TranslationSchema } from "../types";

export const en = {
  common: {
    back: "Back",
    next: "Next",
    save: "Save",
    saved: "Saved ✓",
    delete: "Delete",
    loading: "Loading...",
    error: "Error",
    share: "Share",
    linkCopied: "Link copied!",
    perPerson: "per person",
    perNight: "/night",
    days: "days",
    day: "day",
    travelers: "travelers",
    traveler: "traveler",
    planTrip: "Plan a trip",
    letsGo: "Let's go!",
    continue: "Continue",
    book: "Book",
    reviews: "reviews",
    live: "Live",
    or: "or by email",
    confirm: "Confirm",
    cancel: "Cancel",
    close: "Close",
    search: "Search",
    yes: "Yes",
    no: "No",
    appLoading: "Loading…",
    sessionExpired: "Your session has expired. You've been signed out.",
    signedOut: "You've been signed out",
    seasonLow: "Low season",
    seasonHigh: "High season",
    seasonTip: "Season tip",
    paywallSingleActive: "Single search active — expires {date}",
    paywallNoAccess: "Limited access — pick a plan",
    paywallFreeAvailable: "Discovery plan: 1 free itinerary this month",
    paywallFreeUsed: "This month's free itinerary is used — come back next month or unlock more searches",
    paywallUpgrade: "Upgrade now",
    paywallSingleExpired: "Single search expired on {date}",
    paywallRenew: "Renew",
    countryTopicsTitle: "Stories by",
    countryTopicsHighlight: "country",
    countryTopicsSubtitle: "Tap a country to open the matching forum thread.",
    dismiss: "OK",
    previous: "Previous",
  },
  notif: {
    title: "Notifications",
    markAllRead: "Mark all read",
    empty: "No notifications",
    reply: "New reply to your comment",
    comment: "New comment on your post",
    filterAll: "All",
    filterComments: "Comments",
    filterReplies: "Replies",
  },
  seasons: {
    tag: "Live weather",
    title: "When to go?",
    highlight: "live seasons",
    subtitle: "Real climate data from a live weather API, refreshed automatically.",
    recoLow: "Great time to visit",
    recoHigh: "Crowded right now",
    recoNeutral: "Shoulder season",
  },
  calendar: {
    viewList: "List",
    viewCalendar: "Calendar",
    saveDraft: "Draft",
    updateDraft: "Update",
    draftSaved: "Draft saved",
    draftError: "Failed to save",
    loginToSave: "Sign in to save",
    addActivity: "Activity",
    addPrompt: "Activity name?",
    empty: "No activities planned.",
    removed: "Activity removed",
    dndHint: "Drag to reorder or drop on another day.",
    movedToDay: "Activity moved",
    regenerate: "Regenerate",
    regenerated: "Itinerary regenerated",
    daysKept: "days kept",
    regenConfirmTitle: "Regenerate itinerary?",
    regenConfirmDesc: "Your edits on {n} day(s) will be preserved. Other days will be recreated from your preferences.",
    keepTastes: "Keep my tastes",
    lockPrefs: "Lock my tastes (avoids variation on redraw)",
    tastesLocked: "Tastes locked",
    unlockPrefs: "Preferences locked: uncheck to allow variation",
    untitledActivity: "Activity",
    dragHandle: "Drag",
    removeActivity: "Remove",
  },
  allInOne: {
    tag: "All-in-one",
    title: "Your traveler life,",
    highlight: "in one place",
    subtitle: "Interactive map, country topics, real-time notifications — everything you need.",
    f1Title: "Interactive map",
    f1Desc: "Mark visited countries, zoom freely, attach a story in a few clicks.",
    f2Title: "Auto country topics",
    f2Desc: "Every country you add opens a discussion thread enriched by the community.",
    f3Title: "Live notifications",
    f3Desc: "Real-time in-app alerts for comments and replies, filterable by type.",
    f4Title: "Traveler profile",
    f4Desc: "Bio, avatar, shared trips and a public map browseable by other travelers.",
    f5Title: "Flexible paywall",
    f5Desc: "Single 7-day search, monthly or yearly plans — switch anytime.",
    f6Title: "Offline",
    f6Desc: "Saved itineraries stay available even without a connection.",
  },
  nav: {
    myTrips: "My trips",
    myLists: "My lists",
    login: "Sign in",
    logout: "Out",
    settings: "Settings",
    search: "Search",
    newTrip: "New trip",
    profile: "Profile",
    dashboard: "Dashboard",
    signIn: "Sign in",
    signUp: "Sign up",
    signOut: "Sign out",
    notSignedIn: "Not signed in",
    discover: "Discover",
    account: "My account",
    about: "About",
    discussion: "Discussion",
  },
  home: {
    title: "Plan your",
    titleHighlight: "Grand Departure",
    subtitle: "Your tailor-made trip in a few clicks: cities, countries or road trips, as you wish.",
    roadTripMode: "Road Trip Mode",
    roadTripDesc: "Multiple cities by car",
    departure: "Departure",
    destination: "Destination",
    viewGuide: "View guide",
    noGuideYet: "No guide yet",
    countryUndetected: "Country not detected — check spelling",
    onlinePlaces: "Other places (online search)",
    guidePreviewTitle: "Guide preview",
    viewFullGuide: "View full guide",
    recommendedDuration: "Suggested duration",
    budgetEstimate: "Budget/day",
    customizeInterests: "Interests",
    pickInterests: "Pick your interests",
    generatePreview: "Generate preview",
    refreshPreview: "Refresh preview",
    dailyHighlights: "Day-by-day highlights",
    planMyItinerary: "Plan my itinerary",
    durationIdeal: "Ideal duration for this country",
    durationTooShort: "Stay is a bit short",
    durationLong: "Very long stay, consider multiple regions",
    idealLabel: "ideal",
    fillFieldsFirst: "Please fill in departure, destination and dates first.",
    interests: {
      label: "Interests",
      culture: "Culture",
      food: "Food",
      nature: "Nature",
      beach: "Beaches",
      adventure: "Adventure",
      nightlife: "Nightlife",
      shopping: "Shopping",
      family: "Family",
    },
    departurePlaceholder: "Paris, London, New York...",
    destinationPlaceholder: "Morocco, Barcelona, Japan...",
    dates: "Travel dates",
    selectDates: "Select dates",
    travelers: "Travelers",
    accommodationIncluded: "Accommodation included",
    accommodationDesc: "Accommodation suggestions in the itinerary",
    accommodationBudget: "Accommodation budget",
    addStop: "Add a stop",
    vehicleType: "Vehicle type",
    fuelEstimate: "Estimate based on",
    fuelSuffix: "L/100km at €1.75/L",
    departureTime: "Departure time",
    departureTimeHint: "Used to compute driving hours, breaks and arrival time.",
    fuel: "fuel",
    arrivalLabel: "Arrival",
    stopLabel: "Stop",
    popularTitle: "Popular",
    popularHighlight: "destinations",
    popularSubtitle: "Get inspired for your next trip",
    trendingTitle: "Trending",
    trendingHighlight: "destinations",
    trendingSubtitle: "The most searched destinations right now",
    recommendedTitle: "We",
    recommendedHighlight: "recommend",
    recommendedSubtitle: "Hidden gems for unforgettable travels",
    plane: "Plane",
    train: "Train",
    boat: "Boat",
    car: "Car",
    randomDestination: "Random destination",
    destinationAlt: "travel destination",
    trendingBadge: "Trending",
    logoAlt: "Globr logo — trip planner",
    decreaseTravelers: "Decrease number of travelers",
    increaseTravelers: "Increase number of travelers",
    roadTripPaywallMessage: "Road trips are available starting with the Voyageur plan.",
    viewPlansAction: "View plans",
    stopsQuotaPrefix: "Your",
    stopsQuotaMiddle: "plan allows up to",
    stopsQuotaUnit: "stop(s).",
    stopsQuotaSuffix: "Upgrade to a higher plan to add more.",
    maxStopsReached: "Maximum number of stops reached",
    maxStopsReachedFor: "for the",
    upgradeButton: "Upgrade",
    seasonAnalyzing: "Finding the best time to visit",
    seasonLowHint: "fewer tourists, better prices",
  },

  // Surprise destination quiz
  surprise: {
    button: "Surprise destination",
    title: "Find my surprise destination",
    subtitle: "Answer 6 questions and we'll suggest the perfect trip.",
    resultSubtitle: "Here's your perfect match ✨",
    searching: "Looking for your ideal destination...",
    bestTime: "Best time to visit",
    highlights: "Don't miss",
    retry: "Start over",
    useDestination: "Use this destination",
    errorTitle: "Oops",
    errorDesc: "Could not find a destination, please try again.",
    q: {
      vibe: "What vibe are you looking for?",
      climate: "Which climate do you prefer?",
      budget: "What's your budget?",
      duration: "How long is your trip?",
      activity: "Your favorite activity?",
      continent: "Any preferred continent?",
    },
    vibe: {
      relax: "Pure relaxation",
      adventure: "Adventure",
      culture: "Culture",
      party: "Party",
      romantic: "Romantic",
      family: "Family",
    },
    climate: {
      tropical: "Tropical & hot",
      mild: "Mild & sunny",
      cold: "Cool or cold",
      any: "No preference",
    },
    budget: {
      low: "Low budget",
      mid: "Mid range",
      high: "Premium",
    },
    duration: {
      short: "Weekend",
      medium: "1-2 weeks",
      long: "3+ weeks",
    },
    activity: {
      beach: "Beach",
      hiking: "Hiking",
      food: "Food",
      history: "History",
      city: "City",
      wildlife: "Wildlife",
    },
    continent: {
      europe: "Europe",
      asia: "Asia",
      americas: "Americas",
      africa: "Africa",
      oceania: "Oceania",
      any: "Surprise me",
    },
  },
  budget: {
    budget: "Budget",
    budgetDesc: "Best price",
    value: "Value",
    valueDesc: "Good balance",
    luxury: "Luxury",
    luxuryDesc: "Premium & comfort",
    unique: "Unique",
    uniqueDesc: "Unique experiences",
    camping: "Camping",
    campingDesc: "Under the stars & van",
    autoFix: "Auto-adjust this day",
    autoFixed: "Settings applied: pace set to \"relaxed\" and the most expensive activity removed.",
    exceedTitle: "Daily budget exceeded",
  },
  questionnaire: {
    selectAll: "Select everything that applies to you",
    refineOptional: "Refine (optional)",
    selectOne: "Choose one option",
    createItinerary: "Create itinerary",
    createRoadTrip: "Create road trip",
    interests: "Interests",
    style: "Travel style",
    pace: "Travel pace",
    foodPref: "Food preferences",
    mobility: "Mobility & accessibility",
    accommodation: "Preferred accommodation",
    vibe: "Desired vibe",
    avoid: "Things to avoid",
    dailyBudget: "Daily budget (per person)",
    languages: "Languages spoken",
    culture: "🏛️ Culture & Museums",
    nature: "🌿 Nature & Hiking",
    food: "🍽️ Local cuisine",
    nightlife: "🌙 Nightlife",
    shopping: "🛍️ Shopping",
    beach: "🏖️ Beach & Relaxation",
    adventure: "🧗 Sports & Adventure",
    photo: "📸 Photography",
    history: "📜 History & Heritage",
    art: "🎨 Art & Street art",
    wellness: "🧘 Wellness & Spa",
    wildlife: "🦁 Wildlife & Safari",
    offbeat: "🗺️ Off the beaten path",
    backpacker: "🎒 Backpacker / Budget",
    comfort: "🏨 Comfort",
    luxuryStyle: "💎 Luxury & Premium",
    local: "🏡 Homestay / Authentic",
    chill: "Relaxed — Take it easy",
    balanced: "Balanced — Mix visits & rest",
    intense: "Intense — See as much as possible",
    streetFood: "🥙 Street food & markets",
    localCuisine: "🍲 Traditional local cuisine",
    fineDining: "🍷 Fine dining",
    vegetarian: "🥗 Vegetarian / Vegan",
    cafe: "☕ Cafés & brunches",
    noConstraint: "🚶 No constraints",
    limitedWalking: "🦶 Limited walking",
    wheelchair: "♿ Wheelchair accessible",
    stroller: "👶 Stroller",
    pet: "🐕 With a pet",
    hotel: "🏨 Hotel",
    airbnb: "🏠 Airbnb / Rental",
    hostel: "🛏️ Hostel",
    campingAccom: "⛺ Camping / Van",
    boutique: "✨ Boutique hotel / Riad",
    romantic: "💕 Romantic",
    family: "👨‍👩‍👧 Family",
    friends: "🎉 Friends / Party",
    solo: "🧭 Solo / Introspection",
    hiddenGems: "💎 Hidden gems",
    instagram: "📱 Instagrammable spots",
    crowds: "👥 Crowds & queues",
    touristTraps: "🪤 Tourist traps",
    longWalks: "🦿 Long walks",
    earlyMornings: "😴 Early mornings",
    nothing: "✅ Nothing in particular",
    budgetFree: "Free — Free activities only",
    budgetLow: "< €30/day/pers.",
    budgetMid: "€30-80/day/pers.",
    budgetHigh: "€80-150/day/pers.",
    budgetPremium: "€150+/day/pers.",
    frenchOnly: "🇫🇷 French only",
    english: "🇬🇧 English",
    spanish: "🇪🇸 Spanish",
    localBasics: "🗣️ Local language basics",
    multilingual: "🌍 Multilingual",
    activitiesPerDay: "Activities per day",
    activities3to4: "3-4 — Relaxed pace",
    activities5to6: "5-6 — Moderate pace",
    activities7to8: "7-8 — Active pace",
    activities9plus: "9+ — Intense pace",
    beachLakeDay: "Beach / swimming day",
    beachLakeDayDesc: "Would you like a beach or lake swimming day?",
    beachNone: "No thanks",
    beachHalfDay: "Half-day beach / lake",
    beachFullDay: "Full day beach / lake",
    beachMultiple: "Multiple beach / lake days",
    daySchedule: "Daily schedule",
    dayScheduleDesc: "What time do you want to start and finish?",
    schedule8to20: "8am — 8pm (Early bird)",
    schedule9to21: "9am — 9pm (Standard)",
    schedule10to22: "10am — 10pm (Flexible)",
    schedule10to23: "10am — 11pm (Night owl)",
    schedule11toMid: "11am — Midnight (Party mode)",
    restDays: "Rest days",
    restDays0: "None — full schedule",
    restDays1: "1 day off",
    restDays2: "2 days off",
    restDays3: "3 days off",
  },
  loading: {
    roadTrip: "Planning your road trip...",
    itinerary: "Creating your itinerary...",
    preparing: "Preparing your adventure in",
    step1: "Analyzing the destination...",
    step2: "Finding the best spots...",
    step3: "Selecting restaurants...",
    step4: "Finalizing the itinerary...",
    step5: "Picking accommodations...",
    factDuration: "Duration",
    factDates: "Dates",
    factCurrency: "Currency",
    factVisa: "Visa",
    visaRequired: "Required",
    visaFree: "Not required",
    previewDays: "Itinerary preview",
    galleryTitle: "Destination preview",
    gallery: {
      monuments: "Landmarks",
      beaches: "Beaches",
      neighborhoods: "Neighborhoods",
      activities: "Activities",
      food: "Food",
      nature: "Nature",
    },
    galleryCaptions: {
      monuments: {
        default: "Iconic landmarks of {dest}",
        culture: "Living heritage to explore",
        history: "Walking through {dest}'s past",
        art: "Open-air art and architecture",
        photo: "Bucket-list spots for your shots",
        offbeat: "Hidden, lesser-known landmarks",
      },
      beaches: {
        default: "Coastlines and turquoise waters of {dest}",
        beach: "Perfect beaches to unwind",
        wellness: "Quiet coves to recharge",
        adventure: "Surf, snorkel and dive spots",
        photo: "Jaw-dropping sunset views",
        offbeat: "Secret beaches off the radar",
      },
      neighborhoods: {
        default: "Neighborhoods to wander in {dest}",
        culture: "Streets full of history and soul",
        offbeat: "Local districts off the tourist path",
        shopping: "Shops, markets and indie makers",
        art: "Street art and indie galleries",
        photo: "Photogenic façades and vibes",
      },
      activities: {
        default: "Experiences to live in {dest}",
        adventure: "Outdoor thrills and challenges",
        wellness: "Spa and feel-good breaks",
        nightlife: "{dest} comes alive at night",
        family: "Activities to share with family",
        offbeat: "Unique and unusual experiences",
      },
      food: {
        default: "Must-try flavors of {dest}",
        food: "Tables, markets and local specialties",
        nightlife: "Bars and late-night spots",
        offbeat: "Hidden eateries and local kitchens",
        culture: "Food, a mirror of local culture",
      },
      nature: {
        default: "Natural landscapes around {dest}",
        nature: "Wild spaces and big panoramas",
        adventure: "Hikes, peaks and outdoor adventure",
        wildlife: "Wildlife to spot and observe",
        wellness: "Green escapes to breathe",
        photo: "Epic backdrops for photography",
      },
    },
    editCaption: "Edit caption",
    saveCaption: "Save",
    resetCaption: "Reset",
    captionPlaceholder: "Your custom caption…",
    validateTitle: "Review your preview",
    validateSubtitle: "Tweak the captions then start generating",
    confirmAndGenerate: "Confirm & generate",
    backToQuestions: "Back to questions",
    contributingTags: "Inspired by",
    customCaption: "Custom",
    tipsTitle: "Did you know?",
    tipsSourceGuide: "From the local guide of {destination}",
    tipsSourceVerified: "Verified source",
    tipsSourceLoading: "Reading the destination guide…",
    // Step 1 — Destination analysis
    tip1: "Check the weather and peak season before booking — it changes everything on the ground.",
    tip2: "Learn 5 words in the local language — \"hello\" and \"thank you\" open doors.",
    // Step 2 — Finding spots
    tip3: "Early morning and golden hour: best light for photos and fewer crowds.",
    tip4: "Mix iconic spots with hidden gems for a more authentic experience.",
    // Step 3 — Restaurants
    tip5: "Try street food: often the most authentic and affordable option.",
    tip6: "Ask locals where they eat — the best spots are rarely in guidebooks.",
    // Step 4 — Accommodation
    tip7: "A central neighborhood beats a luxury hotel far out — you save hours every day.",
    tip8: "Read recent reviews: a great place can change in just a few months.",
    // Step 5 — Finalizing
    tip9: "Keep digital backups of your important documents in the cloud.",
    tip10: "Book must-do activities ahead, but leave room for spontaneity.",
  },
  results: {
    transportOptions: "Transport options",
    transportSubtitle: "Select your preferred mode of transport.",
    continueBtn: "Continue — Plan the itinerary",
    roadRoute: "Road route",
    vehicleType: "Vehicle type",
    departureIcon: "Departure",
    arrivalIcon: "Arrival",
    tollIcon: "Toll",
    fuelIcon: "Gas station",
  },
  itinerary: {
    roadTrip: "Road Trip",
    itineraryLabel: "Itinerary",
    summary: "Summary",
    chatPlaceholder: "Modify the itinerary...",
    updated: "Itinerary updated!",
    nightSpot: "Night spot",
    park4night: "Park4Night",
    franciaPassion: "France Passion",
    nightsTitle: "Duration per stop",
    nightsHint: "Set the number of nights per city (the total must match the length of the trip).",
    nightsUnit: "nights",
    nightsMismatch: "Total nights ({n}) ≠ trip duration ({d}). Adjust to match the schedule.",
    noSelectionYet: "No selections yet",
    toggleView: "Toggle view",
  },
  summary: {
    title: "Summary",
    destination: "Destination",
    duration: "Duration",
    travelersLabel: "Travelers",
    transport: "Transport",
    selectedTransport: "Selected transport",
    estimatedBudget: "Estimated budget",
    transportBudget: "Transport",
    activitiesBudget: "Activities",
    foodBudget: "Dining",
    totalEstimated: "Total estimated",
    mapTitle: "Itinerary map",
    pdf: "PDF",
    loginToSave: "Sign in to save",
    tripSaved: "Trip saved!",
    publicLink: "Public link",
    tagline: "YOUR PERSONALIZED ITINERARY",
    printToc: "Contents",
    printDayLabel: "Day",
    activitiesCountLabel: "activities",
    printPageLabel: "Page",
    bonVoyage: "Have a great trip! 🌍✈️",
    printFooterDesc: "Itinerary generated by Globr. Find and edit your trip at mongrandepart.lovable.app",
    allRightsReserved: "All rights reserved",
    recoBadge: "Top pick",
    publicShareRestricted: "Public sharing is available on the Traveler plan and above.",
  },
  auth: {
    welcomeBack: "Welcome back!",
    createAccount: "Create account",
    loginSubtitle: "Sign in to find your trips",
    signupSubtitle: "Sign up to save your itineraries",
    displayName: "Display name",
    namePlaceholder: "Your name",
    email: "Email",
    emailPlaceholder: "you@example.com",
    password: "Password",
    login: "Sign in",
    signup: "Sign up",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    loginSuccess: "Login successful!",
    checkEmail: "Check your email to confirm your registration!",
    authError: "Authentication error",
    forgotPassword: "Forgot password?",
    forgotDesc: "Enter your email to receive a reset link",
    sendResetLink: "Send reset link",
    backToLogin: "Back to login",
    resetEmailSent: "Reset email sent!",
    passwordMismatch: "Passwords do not match",
    passwordTooShort: "Password must be at least 6 characters",
    passwordUpdated: "Password updated!",
    redirecting: "Redirecting...",
    invalidResetLink: "Invalid link",
    invalidResetLinkDesc: "This reset link is invalid or has expired.",
    backToLoginBtn: "Back to login",
    newPassword: "New password",
    newPasswordDesc: "Enter your new password",
    confirmPassword: "Confirm password",
    updatePassword: "Update",
    changePassword: "Change password",
    currentPassword: "Current password",
    passwordChanged: "Password changed successfully!",
    changePasswordDesc: "Enter your new password",
    promoCodeLabel: "Promo code (optional)",
    promoCodePlaceholder: "Enter your promo code",
    promoCodeHint: "Have a promo code? Enter it to get free searches.",
    promoApplied: "Promo code applied successfully!",
    promoInvalid: "Invalid or expired promo code.",
    referralCodeLabel: "Referral code (optional)",
    referralCodePlaceholder: "Enter a friend's code",
    referralCodeHint: "Referred by a friend? Enter their code for bonus searches.",
    referralApplied: "Referral applied! Bonus searches added.",
    referralInvalid: "Invalid referral code.",
    acceptTermsLabel: "I accept the Terms of Use/Sale and acknowledge that any subscription (monthly or annual) is a minimum 12-month commitment, automatically renewed.",
    acceptTermsRequired: "You must accept the Terms to create an account.",
    cguLink: "Terms",
  },
  myTrips: {
    title: "My trips",
    noTrips: "No saved trips",
    noTripsDesc: "Plan your first trip and save it!",
    loadError: "Error loading trips",
    deleteSuccess: "Trip deleted",
    deleteError: "Delete error",
    filterAll: "All",
    filterDrafts: "Drafts",
    filterFinal: "Trips",
    draftBadge: "Draft",
    actionsLabel: "Actions",
    favoriteLabel: "Favorite",
    tripPromptTitle: "How was this trip?",
    tripPromptDesc: "Share your experience in {city} with the community: tips, advice, stories...",
    tripPromptCta: "Share my experience",
    tripPromptDismiss: "No thanks",
    tripPromptDefaultTitlePrefix: "My experience in",
  },
  myLists: {
    title: "My lists",
    noLists: "No lists",
    noListsDesc: "Save searches to lists from your results.",
    noSearches: "No saved searches",
    deleteSuccess: "List deleted",
    searchDeleteSuccess: "Search deleted",
    deleteError: "Delete error",
  },
  preferences: {
    defaultsApplied: "Your preferences have been applied",
    readyTripApplied: "Ready-made trip loaded — tweak anything before generating",
    reuseTitle: "Re-use your preferences?",
    reuseDesc: "You have {n} saved answers on your account. Re-use them now or start fresh.",
    startFresh: "New preferences",
    reuse: "Re-use",
    saveAsDefaults: "My preferences",
    saveAsDefaultsTitle: "Save these filters and tastes as default preferences on my account",
    saved: "Preferences saved to your account",
  },
  discussion: {
    title: "Traveler discussions",
    subtitle: "Connect with the community: experiences, tips, and advice.",
    myProfile: "My profile",
    searchPlaceholder: "Search a topic, a city...",
    newPost: "New topic",
    locationPlaceholder: "Location (city, country...) — optional",
    pickCountry: "Related country — optional",
    publish: "Post",
    hideMap: "Hide map",
    showMap: "Filter on world map",
    empty: "No topics for this filter. Be the first to start the conversation!",
    save: "Save",
    saved: "Saved",
    unsaved: "Removed from your favorites",
    reuseItinerary: "Reuse itinerary",
    report: "Report",
    reply: "Reply",
    loadMoreReplies: "Load {n} more replies",
    replyingTo: "Replying to a comment",
    replyPlaceholder: "Write your reply...",
    reportTitle: "Report this content",
    reportDesc: "Help us keep the community safe. Briefly describe the issue (spam, inappropriate content, false information…).",
    reportPlaceholder: "Reason for report (optional)",
    confirmReport: "Confirm report",
    alreadyReported: "You've already reported this content.",
    reportSent: "Report sent. Thank you!",
    titlePlaceholder: "Title",
    contentPlaceholder: "Share your experience, your question...",
    like: "Like",
    send: "Send",
    titleContentRequired: "Title (3+ chars) and content (10+ chars) required",
    postPublished: "Post published!",
    photosLabel: "Photos",
    addPhotoButton: "Add",
    deletePhotoLabel: "Remove photo",
    photosWord: "photos",
    eachWord: "each",
    photoAlt: "Photo",
    uploadingPhoto: "Uploading photo",
    rateLimited: "You're posting too fast! Try again in a few minutes.",
    paywallDesc: "Traveler discussions are reserved for {plans} subscribers. Share experiences, tips, and advice with the community.",
    paywallPlans: "Explorer and Family",
    paywallCta: "Discover plans",
    cat: {
      all: "All",
      travel: "Travel",
      app: "The App",
      city: "Cities",
      activity: "Activities",
    },
  },
  legal: {
    title: "Legal information",
    mentions: "Legal notice",
    cgu: "Terms of use",
    privacy: "Privacy policy",
  },
  notFound: {
    title: "404",
    message: "Oops! Page not found",
    goHome: "Return to home",
  },
  testimonials: {
    title: "What our",
    titleHighlight: "travelers say",
    subtitle: "+5,000 itineraries created — Join the community",
  },
  footer: {
    mentions: "Legal notice",
    cgu: "Terms of use",
    privacy: "Privacy policy",
    guide: "Our Guide",
    rights: "All rights reserved.",
    tagline: "The travelers' community",
  },
  continents: {
    europe: "Europe",
    africa: "Africa",
    asia: "Asia",
    americas: "Americas",
    oceania: "Oceania",
    middleEast: "Middle East",
  },
  countries: {
    maroc: "Morocco",
    espagne: "Spain",
    japon: "Japan",
    italie: "Italy",
    portugal: "Portugal",
    thailande: "Thailand",
    turquie: "Turkey",
    etatsunis: "United States",
  },
  airport: {
    selectTitle: "Choose an arrival airport",
    selectSubtitle: "Available airports",
    sortedByProximity: "Sorted by proximity from",
    closest: "Closest",
    change: "Change",
  },
  map: {
    clickToSelect: "Click on the map to choose a destination",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    reset: "Reset",
    showAll: "All",
    showVisited: "Visited",
    showUnvisited: "Not yet",
  },
  landing: {
    seoTitle: "Globr — Plan your custom trip",
    seoDescription: "Personalized itineraries, multi-transport comparison, visa guide and interactive maps for 30,000+ cities and countries worldwide.",
    heroTag: "Made by travelers, for travelers",
    heroTitle1: "Travel easy,",
    heroTitle2: "without the agency price tag",
    heroSubtitle: "No more losing hours comparing flights, hotels and activities across ten different tabs. We handle it all and hand you a tailor-made itinerary — up to 70% cheaper than a traditional travel agency.",
    heroPriceBadge: "💶 Up to 70% cheaper than an agency",
    heroTrustNote: "No commitment · Cancel anytime · First itinerary free",
    startFree: "Create my itinerary",
    pricingSingleTitle: "Single search",
    pricingSinglePrice: "€5",
    pricingSingleUnit: "/ 1 search",
    pricingSingleDesc: "Available 7 days on your account",
    pricingSingleItems: "1 generated itinerary,Available for 7 days,No sharing,No forum access",
    pricingSingleCta: "Buy a search",
    pricingPerTitle: "Explorer",
    pricingPerPrice: "€12.99",
    pricingPerUnit: "/ month or €99/year",
    pricingPerDesc: "Most popular",
    pricingPerPopular: "Recommended",
    pricingPerItems: "Unlimited itineraries,Hotels included,Clean PDF export (no watermark),Advanced personalization,Forum & community map access",
    pricingPerCta: "Choose Explorer",
    pricingYearTitle: "Pro / Agency",
    pricingYearPrice: "€29.99",
    pricingYearUnit: "/ month or €249/year",
    pricingYearDesc: "Concierges & travel bloggers",
    pricingYearItems: "Unlimited searches,White-label PDF,API & export,Multi-account,Priority support",
    pricingYearCta: "Choose Pro",
    pricingBillingMonthly: "Monthly",
    pricingBillingYearly: "Yearly",
    pricingBillingSave: "−36%",
    pricingSingleFor: "To try with no commitment — one-time payment",
    pricingPerFor: "For regular travelers — subscription",
    pricingYearFor: "For pros, agencies & creators — subscription",
    pricingPerPriceM: "€12.99",
    pricingPerUnitM: "/ month",
    pricingPerPriceY: "€99",
    pricingPerUnitY: "/ year",
    pricingPerHintM: "No commitment, cancel anytime",
    pricingPerHintY: "That's €8.25/month — 2 months free",
    pricingYearPriceM: "€29.99",
    pricingYearUnitM: "/ month",
    pricingYearPriceY: "€249",
    pricingYearUnitY: "/ year",
    pricingYearHintM: "No commitment, cancel anytime",
    pricingYearHintY: "That's €20.75/month — 2 months free",
    testimonialsTitle: "What",
    testimonialsTitleHighlight: "our travelers say",
    testimonial1Name: "Sophie L.",
    testimonial1Text: "I planned my Italy road trip in 2 minutes. The itinerary was perfect!",
    testimonial1City: "Paris",
    testimonial2Name: "Marc D.",
    testimonial2Text: "The exported PDF is beautiful, I showed it to my whole group and we followed the plan to the letter.",
    testimonial2City: "Lyon",
    testimonial3Name: "Amina K.",
    testimonial3Text: "Choosing Japan as a country and having sorted airports is genius. Huge time saver.",
    testimonial3City: "Brussels",
    faqTitle: "Frequently asked",
    faqTitleHighlight: "questions",
    faq1Q: "How does itinerary generation work?",
    faq1A: "Our travel planner analyzes your destination, dates, budget and preferences to create a tailored program with optimized activities, restaurants and local transport.",
    faq2Q: "Can I choose an entire country?",
    faq2A: "Yes! When you select a country, the app automatically suggests arrival airports sorted by proximity to your first point of interest.",
    faq3Q: "How does Road Trip mode work?",
    faq3A: "Enable Road Trip mode to plan a car journey with up to 8 intermediate stops, automatic fuel and toll calculation, and gas stations along the route.",
    faq4Q: "Can I share my itinerary?",
    faq4A: "Absolutely. Generate a public link anyone can view without creating an account, or export a professional PDF.",
    faq5Q: "What payment methods are accepted?",
    faq5A: "All payments (single search and subscriptions) are processed by Stripe, our PCI-DSS certified secure payment provider. We accept Visa, Mastercard and American Express. No card data is ever stored on our servers.",
    faq6Q: "Can I modify an itinerary after generation?",
    faq6A: "You can save your itineraries to your lists and regenerate with different parameters at any time.",
    ctaTitle: "Ready for the",
    ctaTitleHighlight: "grand departure",
    ctaSubtitle: "No agency, no hidden fees: your tailor-made itinerary in minutes, up to 70% cheaper.",
    ctaBtn: "Plan my trip",
    navHow: "How it works",
    navFeatures: "Features",
    navPricing: "Pricing",
    navFaq: "FAQ",
    plan: "My Space",
    login: "Sign in",
    mockRoute: "Paris → Tokyo",
    mockDays: "7 days",
    mockActivities: "12 activities",
    statsItineraries: "Itineraries created",
    mobileTitle: "Take Globr everywhere",
    mobileDesc: "Install the app on your phone to access it offline, just like a real mobile app.",
    mobileIos1: "Open the site in Safari",
    mobileIos2: "Tap the share icon",
    mobileIos3: "Select 'Add to Home Screen'",
    mobileAndroid1: "Open the site in Chrome",
    mobileAndroid2: "Tap the ⋮ menu",
    mobileAndroid3: "Select 'Add to Home Screen'",
    visaGuideTag: "Practical guide",
    visaGuideTitle: "Visa, work, studies:",
    visaGuideHighlight: "everything before you go",
    visaGuideDesc: "For every country: visa conditions, work permits, student visas, dual nationality, cost of living. Plan your departure with confidence.",
    visaGuideCta: "Browse the visa guide",
    visaGuideLink: "Read the visa guide for",
    howItWorks: "How it works",
    howTitle: "Your trip in",
    howTitleHighlight: "4 simple steps",
    howSubtitle: "From idea to departure — we handle everything.",
    step1Title: "Choose your destination",
    step1Desc: "Country, city or inspiration: explore 30,000+ cities and countries with smart search.",
    step2Title: "Set your dates and budget",
    step2Desc: "Tell us your dates, traveler count and budget for a tailor-made itinerary.",
    step3Title: "Get your itinerary",
    step3Desc: "You get a day-by-day plan with activities, restaurants and transport, ready to follow.",
    step4Title: "Travel with peace of mind",
    step4Desc: "Export to PDF, share with loved ones and access your trip offline.",
    featuresTitle: "Everything you need",
    featuresTitleHighlight: "to travel well",
    featuresSubtitle: "A complete toolkit to plan, compare and enjoy.",
    feature1Title: "Personalized itineraries",
    feature1Desc: "6 to 10 activities per day tuned to your interests, budget and season.",
    feature2Title: "Transport comparison",
    feature2Desc: "Flights, trains, buses and cars compared in real time with direct operator links.",
    feature3Title: "Visa & safety",
    feature3Desc: "Entry rules, vaccines and safety levels kept up to date for 200+ countries.",
    feature4Title: "Road trip mode",
    feature4Desc: "Up to 8 stops with fuel, toll and parking calculations.",
    feature5Title: "Interactive maps",
    feature5Desc: "See your itinerary on a map with activities, hotels and points of interest.",
    feature6Title: "PDF export & sharing",
    feature6Desc: "Elegant 1-day-per-page PDF and secure public link to share in one click.",
    pricingTitle: "Pricing that's",
    pricingTitleHighlight: "simple and clear",
    pricingSubtitle: "Try for free, pay only if you love it.",
    trustTravelers: "12,800+ travelers",
    trustDestinations: "30,000+ cities covered",
    heroCities: "30,000+ cities covered worldwide",
    communityTag: "Community",
    communityTitle: "Join a community",
    communityTitleHighlight: "of passionate travelers",
    communitySubtitle: "Share your itineraries, discover others' and find inspiration in stories from around the world.",
    communityFeat1Title: "World map",
    communityFeat1Desc: "See which countries the community has visited and find new inspiration.",
    communityFeat2Title: "Discussion forum",
    communityFeat2Desc: "Ask questions, share tips and read travel experiences from members.",
    communityFeat3Title: "Traveler profiles",
    communityFeat3Desc: "Follow other globetrotters and get inspired by their public itineraries.",
    communityCta: "Explore the map",
    communityProfileCta: "Browse profiles",
    communityMapHint: "Click a country to explore the community's trips.",
    heroImageAlt: "Globr travel destination — image",
  },
  admin: {
    title: "Administration",
    users: "Users",
    search: "Search...",
    noName: "No name",
    registeredOn: "Registered on",
    loading: "Loading...",
    noUsers: "No users found",
    deleteConfirm: "Delete",
    roleAdded: "Role added",
    roleRemoved: "Role removed",
    loadError: "Loading error",
    deleteSuccess: "User deleted",
    deleteError: "Delete error",
    back: "Back",
  },
  shared: {
    title: "Shared itinerary",
    notFound: "Itinerary not found",
    notFoundDesc: "This share link is invalid or the itinerary has been deleted.",
    backHome: "Back to home",
    createdWith: "Created with Globr",
    back: "Back",
    share: "Share",
    linkCopied: "Link copied!",
    destination: "Destination",
    duration: "Duration",
    travelers: "Travelers",
    departure: "Departure",
    days: "{n} days",
    day: "Day",
    planTrip: "Plan my trip",
    filtersTitle: "Filters & tastes for this trip",
  },

  settings: {
    title: "Settings",
    profile: "Profile",
    displayName: "Display name",
    connectedVia: "Connected via",
    changeEmail: "Change email",
    changeEmailDesc: "A confirmation email will be sent to both addresses.",
    newEmail: "New email",
    updateEmail: "Update email",
    emailConfirmSent: "A confirmation email has been sent.",
    changePassword: "Change password",
    changePasswordDesc: "Minimum 6 characters.",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    updatePassword: "Update password",
    passwordMismatch: "Passwords do not match.",
    passwordTooShort: "Password must be at least 6 characters.",
    passwordUpdated: "Password updated successfully.",
    nameUpdated: "Name updated.",
    profilePhoto: "Profile photo",
    profilePhotoDesc: "Click to change (max 2MB)",
    avatarUpdated: "Profile photo updated.",
    subscription: "Subscription",
    subscriptionDesc: "Manage your plan and payments.",
    freePlan: "No active subscription",
    freePlanDesc: "1 free itinerary every month — or switch to a single search (€5) or a Voyageur, Explorer or Pro subscription via Stripe.",
    singleSearchPaid: "Search unlocked for 7 days!",
    offlineReassurance: "Every trip, even on the free Discovery plan, stays available offline.",
    active: "Active",
    upgradePlan: "Upgrade plan",
    perSearchPlan: "Per search",
    perSearchDesc: "One-time payment for a full itinerary search.",
    buySearch: "Buy a search",
    annualPlan: "Unlimited annual",
    annualDesc: "Unlimited searches for 1 year.",
    subscribe: "Subscribe",
    bestValue: "Best value",
    renewsOn: "Renews on",
    manageSubscription: "Manage my subscription",
    refreshStatus: "Refresh status",
    checkoutSuccess: "Payment successful! Your subscription is active.",
    checkoutCancelled: "Payment cancelled.",
    signOut: "Sign out",
    deleteAccount: "Delete my account",
    deleteAccountTitle: "Permanently delete your account?",
    deleteAccountWarning: "This action is irreversible. All your data (profile, trips, lists, searches, contributions, avatar photos) will be permanently deleted in compliance with GDPR.",
    deleteAccountTypeToConfirm: "Type DELETE to confirm:",
    deleteAccountConfirm: "Delete permanently",
    deleteAccountConfirmRequired: "Type DELETE to confirm",
    accountDeleted: "Account deleted.",
    deleteAccountHint: "Deletion is permanent and GDPR-compliant.",
    referral: "Referral",
    referralDesc: "Share your code and earn free searches.",
    yourReferralCode: "Your referral code",
    copyCode: "Copy",
    codeCopied: "Code copied!",
    referralCount: "Referrals",
    referralBonus: "bonus searches earned",
    shareReferral: "Share my code",
    paymentPending: "Payment is being validated…",
    avatarMaxSize: "Maximum size: 2MB",
    avatarUploadError: "Error uploading photo",
    checkoutError: "Payment error",
    deleteAccountError: "Error deleting account",
    deleteAccountConfirmWord: "DELETE",
    manageFamilyProfiles: "Manage Family profiles",
    newEmailPlaceholder: "new@email.com",
    singleSearchTitle: "One-time search",
    noCommitment: "No commitment",
    perSearchUnit: "/ 1 search",
    singleSearchFeature1: "1 generated itinerary",
    singleSearchFeature2: "Visible on your account for 7 days",
    singleSearchFeature3: "No sharing, no forum access",
    buySingleSearchButton: "Buy a search (€5)",
    billingMonthly: "Monthly",
    billingYearly: "Yearly",
    billingYearlyDiscount: "up to 36% off",
    recommendedBadge: "Recommended",
    plans: {
      voyageur: {
        name: "Traveler",
        feature1: "10 searches/month",
        feature2: "PDF export",
        feature3: "Road trip up to 5 stops",
        feature4: "Unlimited sharing",
        cta: "Choose Traveler",
      },
      explorer: {
        name: "Explorer",
        feature1: "40 searches/month",
        feature2: "Road trip up to 8 stops",
        feature3: "Unlimited family profiles",
        feature4: "Priority moderation",
        cta: "Choose Explorer",
      },
      pro: {
        name: "Pro / Agency",
        feature1: "Unlimited searches",
        feature2: "White-label PDF",
        feature3: "API / export",
        feature4: "Multiple accounts",
        cta: "Choose Pro",
      },
    },
    referralShareTextPrefix: "Join Globr with my code",
    referralShareTextSuffix: "and get free searches!",
  },

  signupBanner: {
    title: "Create your account to continue",
    desc: "Sign up to save your itineraries and access searches via Stripe (single search or subscription).",
    cta: "Create my account",
    login: "I already have an account",
  },

  guide: {
    tag: "Practical Guide",
    title: "Visa &",
    titleHighlight: "Admin Guide",
    subtitle: "Everything you need to know about visas, work permits, studies and expatriation for each destination.",
    searchPlaceholder: "Search a country...",
    allContinents: "All continents",
    visaRequired: "Visa required",
    visaFree: "Visa free",
    tourism: "Tourism",
    tourismShort: "Tourism",
    work: "Work",
    workShort: "Work",
    study: "Study",
    studyShort: "Study",
    live: "Live there",
    liveShort: "Live",
    visaStatus: "Visa status",
    duration: "Authorized stay",
    cost: "Cost",
    requirements: "Requirements",
    tips: "Practical tips",
    applyEVisa: "Apply for e-Visa",
    dualNationality: "Dual nationality",
    allowed: "Allowed",
    notAllowed: "Not allowed",
    healthSystem: "Health system",
    costOfLiving: "Cost of living",
    disclaimer: "This information is provided as a general guide and may change. Always check official government websites for the most up-to-date requirements before traveling.",
    translating: "Translating…",
    currencyTitle: "Currency converter",
    currencyError: "Exchange rate unavailable. Try again later.",
    currencySwap: "Swap conversion",
    currencyLive: "Rate updated daily",
    currencyRefresh: "Refresh rate",
    currencyDisclaimer: "Indicative rate provided for information only. Source:",
    currencyUpdatedOn: "updated on",
    eurCountryTitle: "This country uses the euro (€)",
    eurCountryNote: "No converter needed: the local currency is the same as yours if you're traveling from the eurozone.",
    lastTranslated: "Last translated",
    clearTranslationCache: "Force a new translation",
    cacheBannerTitle: "Showing data from local cache",
    cacheBannerDesc: "Translation and exchange rate may be outdated. Refresh to fetch the latest values.",
    cacheBannerRefresh: "Refresh all",
    cacheBannerRefreshing: "Refreshing…",
    cacheBannerRefreshingDesc: "Fetching the latest translation and exchange rate.",
    cacheTranslationLabel: "Translation cached on",
    cacheFxLabel: "Exchange rate cached on",
    cacheStale: "expired",
    enrichmentTitle: "Practical info to prepare your departure",
    enrichLanguages: "Spoken languages",
    enrichTimezone: "Time zone",
    enrichElectricity: "Electricity",
    enrichBestTime: "Best time to visit",
    enrichEmergency: "Emergency numbers",
    enrichSafety: "Safety",
    enrichPopularCities: "Popular cities",
    enrichSalary: "Salaries",
    enrichTaxation: "Taxation",
    enrichConnectivity: "Connectivity",
    enrichTransport: "Transport",
    enrichItineraries: "Sample itineraries",
    budgetBackpacker: "Backpacker budget / day",
    budgetMid: "Comfort budget / day",
    budgetComfort: "Premium budget / day",
    enrichCulturalTips: "Cultural tips",
    enrichScams: "Common scams",
    enrichHealth: "Health & vaccines",
    enrichAccommodation: "Finding a place",
    enrichApps: "Useful apps",
    enrichOfficial: "Official websites",
    safetyLevel: { low: "Low risk", medium: "Moderate caution", high: "High caution" },
    enrichEmbassy: "Embassy / Consulate",
    embassyAddress: "Address",
    embassyPhone: "Phone",
    embassyWebsite: "Official website",
    safetyRecommendationsTitle: "Recommendations by risk level",
    safetyRecos: {
      low: [
        "Standard vigilance in touristy areas (pickpockets).",
        "Keep a digital copy of your documents (passport, visa).",
        "Take out a basic travel insurance policy.",
      ],
      medium: [
        "Avoid walking alone at night in quiet districts.",
        "Prefer official ride-hailing apps over street-hailed taxis.",
        "Check your government travel advisory regularly before and during the trip.",
        "Choose travel insurance covering medical evacuation.",
      ],
      high: [
        "Register with your country's traveler registration program.",
        "Limit overland travel; prefer flights between regions.",
        "Avoid public gatherings and sensitive border areas.",
        "Keep your embassy emergency contacts easily accessible.",
        "Take out comprehensive insurance including repatriation and political risk cover.",
      ],
    },
    faqTitleEnrich: "Frequently asked questions about",
    jumpToHealth: "Jump to health section",
    jumpToAccommodation: "Jump to accommodation section",
    jumpToTransport: "Jump to transport section",
    jumpToSafety: "Jump to safety section",
    jumpToCurrency: "Jump to currency converter",
    relatedSections: "Related sections",
    faq: {
      visaQ: "Do I need a visa for {country}?",
      visaA: "{answer}",
      visaAYes: "Yes, a visa is required. Duration: {duration}.",
      visaANo: "No, {country} is visa-free for many nationalities. {visaFreeFor} Duration: {duration}.",
      durationQ: "How long can I stay?",
      durationA: "{duration}",
      bestTimeQ: "When is the best time to visit?",
      bestTimeA: "{bestTime}",
      currencyQ: "What currency is used in {country}?",
      currencyA: "The local currency is the {name} ({code}, symbol {symbol}).",
      currencyEurA: "{country} uses the euro (€), no conversion needed from the eurozone.",
      safetyQ: "Is the country safe for travelers?",
      safetyA: "{note}",
      healthQ: "Are any specific vaccines required?",
      healthA: "{advice}",
      languageQ: "What languages are spoken?",
      languageA: "Main languages: {langs}.",
      costQ: "What is the cost of living like?",
      costA: "{cost}",
    },
    breadcrumbHome: "Home",
    currency: "Currency",
    practical: "Practical info",
    h1: "Complete guide to visas and administrative procedures",
    h1Country: "Guide & tips",
    sectionTourism: "Tourist visa",
    sectionWork: "Working there",
    sectionStudy: "Studying there",
    sectionLive: "Settling long-term",
    seoTitle: "Travel Guide & Tips: visa, safety, health, budget | Globr",
    seoDescription: "Practical guide and tips for 27+ countries: visa, safety, health, budget, transport, culture and cost of living. Everything to prepare your trip with confidence.",
    seoTitleCountry: "{country}: travel guide & tips (visa, safety, budget) | Globr",
    seoDescriptionCountry: "Complete guide for {country}: visa and procedures, safety, health, budget, transport, culture and practical tips to prepare your trip.",
    sectionsNav: "Guide sections",
    clearSearch: "Clear",
    continentFiltersNav: "Continent filters",
    tocNav: "Table of contents",
  },

  nav2: {
    guide: "Our Guide",
  },

  plan: {
    recapTitle: "Trip recap",
    recapSubtitle: "Review the details before generating your full itinerary.",
    budget: "Budget level",
    travelMode: "Travel mode",
    flightMode: "Flight / Train",
    noInterests: "No interests selected",
    openGuideTitle: "Open the full guide",
    openGuideDesc: "Visa, health, safety, cost of living and practical tips.",
    disclaimer: "This preview is indicative. The final itinerary is generated specifically from your criteria.",
    editTrip: "Edit",
    launchSearch: "Launch search",
  },
  offline: {
    title: "Offline",
    intro: "Trips available without a connection:",
    youAreOffline: "You're offline — here's what's available:",
    empty: "No offline trips yet",
    emptyDesc: "From My trips, choose “Keep offline”.",
    keepAction: "Keep offline",
    removeAction: "Remove from offline",
    saved: "Available offline",
    removed: "Removed from offline",
    viewItinerary: "View itinerary",
    syncNow: "Sync now",
    synced: "Trips synced",
    upToDate: "Already up to date",
    syncError: "Sync failed",
    lastSync: "Last sync",
    serverDeletedTitle: "Deleted on server",
    serverDeletedDesc: "These trips no longer exist on the server. Remove them from the offline cache?",
    keep: "Keep",
    remove: "Remove",
    confirmRemoveTitle: "Remove from offline?",
    confirmRemoveDesc: "This trip was deleted on the server. Removing it from the cache will erase the local copy permanently.",
    conflictsTitle: "Sync conflicts",
    conflictsDesc: "The server version overwrote your offline edits. A local copy was kept:",
  },

  plans: {
    free: { shortLabel: "Discovery" },
    voyageur: { shortLabel: "Voyageur" },
    explorer: { shortLabel: "Explorer" },
    pro: { shortLabel: "Pro / Agency" },
  },

  theme: {
    enableLight: "Switch to light theme",
    enableDark: "Switch to dark theme",
  },

  mobile: {
    home: "Home",
    quickSearchPlaceholder: "Where do you want to go?",
    quickSearchLabel: "Quick search",
    quickStart: "Quick start",
  },

  routeMap: {
    departure: "Departure",
    arrival: "Arrival",
    tollZone: "Toll zone",
  },

  activityCard: {
    bookRide: "Book a ride:",
    transitMiniPlan: "Mini transit plan",
    departure: "Departure",
    arrival: "Arrival",
    doorToDoor: "Door-to-door",
    margin: "Buffer",
    understandMargin: "Understand the buffer",
    show: "show",
    hide: "hide",
    advanceHoursTitle: "Recommended lead time by mode of transport",
    modeFlight: "Flight",
    modeTrain: "Train",
    modeFerry: "Ferry",
    modeBus: "Bus",
    advance: {
      flight: {
        time: "+2h to 3h",
        usage: "Arrive 2h early (Schengen) or 3h early (international): check-in, bag drop, security.",
      },
      train: {
        time: "+20 to 30 min",
        usage: "Arrive at the station 20–30 min early: pick up your ticket, reach the platform, find your seat.",
      },
      ferry: {
        time: "+1h to 2h",
        usage: "1h on foot, 2h with a vehicle: boarding, checks, loading.",
      },
      bus: {
        time: "+15 to 20 min",
        usage: "Arrive 15–20 min early: find the platform, stow luggage, board.",
      },
      car: {
        time: "+15 min",
        usage: "Buffer for traffic, parking, and a fuel stop before you set off.",
      },
      default: {
        time: "+30 min",
        usage: "Standard safety buffer to absorb delays and connections.",
      },
    },
    marginExplainer: "This buffer is added to the door-to-door time to work out your departure time and keep the transition between cities stress-free.",
    compareOptionsTitle: "Route options — compare before confirming",
    fast: "Fast",
    cheap: "Budget",
    recommended: "Recommended",
    chosen: "Selected",
    toConfirm: "To confirm",
    advancePrefix: "Lead time",
    comparisonTitle: "Comparison before confirming",
    current: "Current",
    plannedChoice: "Planned choice",
    durationDeltaLabel: "Duration",
    budgetDeltaLabel: "Day budget",
    before: "Before",
    after: "After",
    same: "unchanged",
    unchangedHalfDays: "Half-days unchanged",
    marginVsSuffix: "vs {margin} currently",
    confirmChoice: "Confirm this choice",
  },

  saveToList: {
    trigger: "List",
    title: "Save to a list",
    createPlaceholder: "Create a new list...",
    createError: "Error creating list",
    saveError: "Error saving",
    searchSaved: "Search saved!",
    emptyState: "Create your first list above",
  },

  shareLinks: {
    ttl7: "7 days",
    ttl30: "30 days",
    ttl90: "90 days",
    ttl365: "1 year",
    title: "Public share links",
    description: "Each link expires automatically and can be regenerated or revoked at any time.",
    duration: "Duration",
    create: "Create",
    empty: "No active link. Create one above.",
    statusActive: "Active",
    statusRevoked: "Revoked",
    statusExpired: "Expired",
    createdOn: "Created on",
    expiresOn: "Expires on",
    revokedOn: "Revoked on",
    copy: "Copy",
    regenerate: "Regenerate",
    revoke: "Revoke",
    revokeConfirm: "Revoke this link? It will no longer be accessible.",
    linkCopied: "Link copied!",
    linkCreatedCopied: "Link created and copied",
    tokenRegenerated: "Token regenerated and copied — the old one no longer works",
    linkRevoked: "Link revoked",
    genericError: "Error",
  },

  family: {
    title: "Family Profiles",
    subtitle: "Create up to 5 profiles to generate itineraries that blend the whole family's tastes.",
    subscriptionRequired: "Subscription required",
    voyageurLimitMessage: "The Traveler plan allows 1 profile. Upgrade to Explorer for unlimited profiles.",
    limitReached: "Limit reached",
    nameRequired: "Name required",
    profileAdded: "Profile added",
    profileDeleted: "Profile deleted",
    upsellTitle: "Family feature",
    upsellDescPart1: "Upgrade to",
    upsellVoyageurOffer: "Traveler (€6.99/month)",
    upsellDescPart2: "for 1 profile, or",
    upsellExplorerOffer: "Explorer (€12.99/month)",
    upsellDescPart3: "for unlimited family profiles.",
    viewPlansButton: "View plans",
    addProfileTitle: "Add a profile",
    firstName: "First name",
    age: "Age (optional)",
    avatarLabel: "Avatar",
    interestsLabel: "Interests",
    addButton: "Add",
    noProfiles: "No profiles yet.",
    yearsUnit: "years old",
  },

  contribute: {
    requiredFieldsError: "Title, description and city are required",
    licenseRequiredError: "Please accept the CC BY 4.0 license to publish your contribution",
    uploadingPhoto: "Uploading photo",
    updatingStatus: "Updating…",
    savingStatus: "Saving…",
    updateSuccess: "Contribution updated",
    submitSuccess: "Contribution submitted — pending moderation",
    submitError: "Error submitting contribution",
    deleteConfirm: "Delete this contribution?",
    deleteSuccess: "Deleted",
    withdrawSuccess: "Withdrawal request sent to the moderation team",
    headerTitle: "Contribute",
    heroTitle: "Share your hidden gems",
    heroSubtitle: "Help other travelers by sharing the activities and restaurants you loved. Every contribution is reviewed by our team before publication.",
    tabNew: "New",
    tabMine: "My contributions",
    editingNotice: "Editing a pending contribution",
    typeLabel: "Type",
    typeActivity: "Activity",
    typeRestaurant: "Restaurant",
    mainInfoTitle: "Main information",
    placeName: "Place name",
    placeNamePlaceholderRestaurant: "E.g.: Marcel's Bistro",
    placeNamePlaceholderActivity: "E.g.: Hidden waterfall of Mont Sacré",
    descriptionLabel: "Description",
    descriptionPlaceholder: "Why is it great? What should people do or eat there?",
    cityLabel: "City",
    cityPlaceholder: "Lisbon",
    countryLabel: "Country",
    countryPlaceholder: "Portugal",
    addressLabel: "Exact address",
    photosLabel: "Photos",
    photoAltPrefix: "Photo of",
    photoAltGeneric: "Photo of the place",
    deletePhotoLabel: "Delete photo",
    addPhotoButton: "Add",
    photosWord: "photos",
    eachWord: "each",
    tipsCardTitle: "Practical tips",
    tipsLabel: "Tips & tricks",
    tipsPlaceholder: "Book in advance, ask for the chef's specialty...",
    bestHoursLabel: "Best hours",
    bestHoursPlaceholder: "9-11am or after 7pm",
    crowdLevelLabel: "Crowd level",
    crowdLevelPlaceholder: "Level",
    crowdLow: "Quiet",
    crowdMedium: "Moderate",
    crowdHigh: "Very busy",
    isPaidLabel: "Paid?",
    avgPriceLabel: "Average price / person (€)",
    licenseCardTitle: "License & attribution",
    authorNameLabel: "Displayed author name (optional)",
    authorNamePlaceholder: "Nickname, first name, or leave blank",
    authorNameHint: "If left blank, your profile nickname will be used for public attribution.",
    licenseAgreementPrefix: "I am publishing under the",
    licenseAgreementBody: "I retain ownership of my content (text + photos). I allow Globr and other travelers to reuse it as long as my attribution is credited. I certify I hold the rights to the uploaded photos and can request removal at any time.",
    preparingStatus: "Preparing…",
    updateButton: "Update",
    submitButton: "Submit for moderation",
    noContributions: "No contributions yet.",
    statusPending: "Pending",
    statusApproved: "Approved",
    statusRejected: "Rejected",
    statusWithdrawalRequested: "Withdrawal requested",
    statusWithdrawn: "Withdrawn",
    reasonLabel: "Reason:",
    withdrawalReasonLabel: "Withdrawal reason:",
    editTitle: "Edit",
    requestWithdrawalTitle: "Request withdrawal",
    withdrawButton: "Withdraw",
    withdrawDialogDesc: "Your contribution will be immediately hidden from the public and sent to the moderation team for permanent deletion (including photos).",
    withdrawReasonLabel: "Reason (optional)",
    withdrawReasonPlaceholder: "Place closed, photos to remove, etc.",
    confirmWithdrawButton: "Confirm withdrawal",
  },

  esim: {
    title: "Recommended eSIMs for",
    subtitle: "Selection ranked by price, user base and network quality, with a bonus for local coverage.",
    cardsViewTitle: "Card view",
    cardsViewLabel: "Cards",
    compareViewTitle: "Comparison view",
    compareViewLabel: "Compare",
    hideFilters: "Hide filters",
    showFilters: "Filters & details",
    filtersLabel: "Filters",
    resetButton: "Reset",
    maxPriceLabel: "Max price",
    minNetworkLabel: "Min. network quality",
    minReputationLabel: "Min. reputation (M users)",
    allLabel: "All",
    matchingProvidersLabel: "provider(s) match your filters.",
    defaultRankingNotice: "Showing the default ranking.",
    criteriaHeader: "Criterion",
    bestChoiceBadge: "Best choice",
    internalScoreLabel: "Internal score:",
    scoreTooltipSuffix: "(price, network, user base, regional coverage)",
    scoreLabel: "Score",
    priceLabel: "Price",
    networkLabel: "Network",
    userBaseLabel: "User base",
    usersUnit: "users",
    strongRegionsLabel: "Strong regions",
    strengthLabel: "Strength",
    linkHeader: "Link",
    viewOffersLink: "View offers",
    baseLabel: "Base",
    scoreDetailsLabel: "Score details",
    exampleLabel: "E.g.:",
    pricingDisclaimer: "Indicative pricing · check the exact plan on the provider's site before purchase.",
  },

  roadtrip: {
    totalDistance: "Total distance",
    drivingTime: "Driving time",
    breakTime: "Breaks",
    stopTime: "Stopovers",
    optimizedOrder: "Optimised order",
    ofDriving: "driving",
    stopAt: "Stop in",
    arrival: "Arrival",
    departureDate: "Departure date",
    departureTime: "Departure time",
    stopDuration: "Time per stop",
    dieselPrice: "Diesel",
    livePrice: "live price",
    stationsNearby: "stations along the route",
    estimatedPrice: "estimated price (outside France)",
    budgetTitle: "Total road trip budget",
    budgetFuel: "Fuel",
    budgetTolls: "Tolls",
    budgetStay: "Accommodation",
    budgetMeals: "Meals",
    budgetPerPerson: "Per person",
    budgetTravelers: "travellers",
    budgetTraveler: "traveller",
    budgetDays: "days",
    budgetNights: "nights",
    budgetNote: "Estimate: fuel and tolls from the actual route, accommodation and meals estimated from your budget level.",
    break: {
      break: "Break",
      meal: "Meal",
      fuel: "Refuel",
    },
  },

  carRoute: {
    tollSectionsLabel: "toll section(s)",
    noTolls: "No tolls",
    trafficDelaySuffix: "min of traffic",
    smoothTraffic: "Smooth traffic",
    fuelStopsLabel: "recommended fuel stop(s)",
  },

  blog: {
    seoTitle: "Travel blog: tips, guides and advice | Globr",
    seoDescription: "Travel tips, country guides and practical advice: visa, budget, safety, eSIM and transport to prepare your next trip.",
    h1: "Travel blog: tips and practical guides",
    intro: "Welcome to the Globr blog. Browse our practical country guides — visa, budget, safety, eSIM, transport — and our tips to plan your trip with peace of mind.",
  },
} satisfies TranslationSchema;
